import { Box, Drawer, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUpdateFileMutation } from 'api/mutations/useUpdateFileMutation';
import { useCategoriesListQuery } from 'api/queries/useCategoriesListQuery';
import { useFoldersQuery } from 'api/queries/useFoldersQuery';
import { alert, SelectController } from 'components';
import { useWindowSize } from 'hooks';
import { theme } from 'styles';
import { isSystemCategory } from 'validations';

import {
  CancelButton,
  Dialog,
  DuplicateButton,
  FooterActions,
  StyledTextField,
} from '../FolderEditCategoryModal/styled';

interface EditFileModalProps {
  open: boolean;
  onClose: () => void;
  fileId: string;
  currentName: string;
  currentLocations: { category_id: string; folder_id: string }[];
}

const labelStyle = {
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.secondary.dark,
  marginBottom: '6px',
  display: 'block',
};

const selectStyle = {
  borderRadius: '8px',
  fontSize: '14px',
  backgroundColor: theme.palette.white.main,
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${theme.palette.info.main}`,
  },
};

export function EditFileModal({
  open,
  onClose,
  fileId,
  currentName,
  currentLocations,
}: EditFileModalProps) {
  const [name, setName] = useState(currentName);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    currentLocations.length === 1 ? currentLocations[0].folder_id ?? null : null,
  );

  const { control, watch, setValue } = useForm<{ categoryIds: string[] }>({
    defaultValues: { categoryIds: currentLocations.map((l) => l.category_id) },
  });
  const watchedCategoryIds = watch('categoryIds');
  const selectedCategoryIds = useMemo(() => watchedCategoryIds ?? [], [watchedCategoryIds]);

  const { updateFileMutation, isLoading } = useUpdateFileMutation();
  const { categoriesList } = useCategoriesListQuery();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const singleCategoryId = selectedCategoryIds.length === 1 ? selectedCategoryIds[0] : undefined;
  const { folders } = useFoldersQuery({ categoryId: singleCategoryId });

  useEffect(() => {
    if (open) {
      setName(currentName);
      setValue(
        'categoryIds',
        currentLocations.map((l) => l.category_id),
      );
      setSelectedFolderId(
        currentLocations.length === 1 ? currentLocations[0].folder_id ?? null : null,
      );
    }
  }, [open, currentName, currentLocations, setValue]);

  const handleClose = () => {
    setName('');
    setValue('categoryIds', []);
    setSelectedFolderId(null);
    onClose();
  };

  const handleSave = async () => {
    await updateFileMutation({
      id: fileId,
      display_name: name.trim(),
      category_ids: selectedCategoryIds,
      ...(selectedFolderId && { folder_id: selectedFolderId === '/' ? null : selectedFolderId }),
    });
    alert.success(t('toasts.file.success.edit'));
    handleClose();
  };

  const formContent = (
    <Box sx={{ px: 2, pb: 'env(safe-area-inset-bottom)' }}>
      <Typography variant="h6" fontWeight={700} fontSize="16px" mb={3}>
        {t('resources.documentCategories.files.editFileModal.title')}
      </Typography>

      <Box mb={2}>
        <Typography
          variant="body2"
          fontSize="14px"
          fontWeight={500}
          color="text.secondary"
          mb={0.75}
        >
          {t('resources.documentCategories.files.editFileModal.placeholders.fileName')}
        </Typography>
        <StyledTextField
          fullWidth
          placeholder={t('resources.documentCategories.files.editFileModal.placeholders.fileName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Box>

      <Box mb={2}>
        <Typography component="span" sx={labelStyle}>
          {t('resources.documentCategories.files.editFileModal.placeholders.selectCategory')}
        </Typography>
        <SelectController
          name="categoryIds"
          control={control}
          placeholder={t(
            'resources.documentCategories.files.editFileModal.placeholders.selectCategory',
          )}
          options={categoriesList
            .filter((category) => category.tag && !isSystemCategory(category.tag))
            .map((cat) => ({
              value: cat.id,
              label: cat.title,
            }))}
          multipleSelect
          type="poll"
          handleChange={() => {
            if (selectedCategoryIds.length !== 1) setSelectedFolderId(null);
          }}
          sx={{
            height: 'auto',
            minHeight: '44px',
            borderRadius: '4px',
            background: theme.palette.white.main,
            '.MuiOutlinedInput-notchedOutline': { border: 'none' },
            '.MuiSelect-select.MuiSelect-select': {
              height: 'auto',
              padding: '8px 0px 8px 0px',
              '& > div': { margin: 0 },
            },
            '.MuiSvgIcon-root.MuiSelect-icon': { top: '50%', transform: 'translateY(-50%)' },
          }}
        />
      </Box>

      {selectedCategoryIds.length === 1 && (
        <Box mb={2}>
          <Typography component="span" sx={labelStyle}>
            {t('resources.documentCategories.files.editFileModal.selectFolder')}
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={selectedFolderId ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              setSelectedFolderId(e.target.value || null);
            }}
            MenuProps={{
              PaperProps: { style: { maxHeight: 250 } },
            }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography color="text.disabled" fontSize="14px">
                    {t(
                      'resources.documentCategories.files.editFileModal.placeholders.selectFolder',
                    )}
                  </Typography>
                );
              }
              const folder = folders.find((f) => f.id === value);
              return folder?.name ?? value;
            }}
            sx={selectStyle}
          >
            {folders.length === 0 && (
              <MenuItem value="" disabled>
                {t('resources.documentCategories.files.editFileModal.placeholders.selectFolder')}
              </MenuItem>
            )}
            <MenuItem key="/" value="/" sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography fontSize="14px">
                {categoriesList.find((cat) => cat.id === currentLocations?.[0]?.category_id)?.title}
              </Typography>
              <Typography fontSize="11px" color="text.disabled" lineHeight={1.2}>
                /
              </Typography>
            </MenuItem>
            {folders.map((folder) => (
              <MenuItem
                key={folder.id}
                value={folder.id}
                sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <Typography fontSize="14px">{folder.name}</Typography>
                {folder.path && (
                  <Typography fontSize="11px" color="text.disabled" lineHeight={1.2}>
                    {folder.path}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <FooterActions>
        <CancelButton variant="outlined" onClick={handleClose}>
          {t('resources.documentCategories.files.editFileModal.buttons.cancel')}
        </CancelButton>
        <DuplicateButton
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || selectedCategoryIds.length === 0 || isLoading}
        >
          {t('resources.documentCategories.files.editFileModal.buttons.save')}
        </DuplicateButton>
      </FooterActions>
    </Box>
  );

  return isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          pb: 'env(safe-area-inset-bottom)',
          paddingBottom: '16px',
          maxHeight: '90dvh',
          overflowY: 'auto',
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: '2px',
          mx: 'auto',
          my: '12px',
        }}
      />
      {formContent}
    </Drawer>
  ) : (
    <Dialog open={open} onClose={handleClose}>
      {formContent}
    </Dialog>
  );
}
